import "#vendor/ahoy.js";
import debounce from "lodash/debounce";
import { randomUUID } from "#/shared/utils/string";
import { getApiHost } from "#/shared/api/api-host";
import { AnalyticsEventQueue } from "./event-queue";
import { AnalyticsEventTransport } from "./event-transport";
import { AnalyticsEventOutbox } from "./event-outbox";
import { AnalyticsEventDelivery } from "./event-delivery";
import { AnalyticsEventFallback } from "./event-fallback";
import { AnalyticsEventClaims } from "./event-claims";
import { reportClientError } from "#ce/utils/error-report";
import { createScrollDepthTracker } from "./scroll-depth";

const FORWARDED_CLICKS = Symbol.for("sparkle.analytics.forwarded-clicks");
const RECORDERS = Symbol.for("sparkle.analytics.recorders");
window.Sparkle ??= {};

let recorders;

/** Records a search and resets scroll-depth thresholds. */
export function trackSearch(subtype, query, requestUrl, resultCount, eventDetail) {
  recorders?.search(subtype, query, requestUrl, resultCount, eventDetail);
}

/** Records a selected search result. */
export function trackSearchSelect(subtype, query, requestUrl, recordType, recordId, eventDetail) {
  recorders?.selection("$search-select", subtype, query, requestUrl, recordType, recordId, eventDetail);
}

/** Records the latest autocomplete query after one second of inactivity per subtype. */
export function trackAutocomplete(subtype, query, suggestions, requestUrl, eventDetail) {
  recorders?.autocomplete(subtype, query, suggestions, requestUrl, eventDetail);
}

/** Records a selected autocomplete result. */
export function trackAutocompleteSelect(subtype, query, requestUrl, recordType, recordId, eventDetail) {
  recorders?.selection("$autocomplete-select", subtype, query, requestUrl, recordType, recordId, eventDetail);
}

/** Records a `$view` unless analytics is disabled or `window.NOTRACK` is set. */
export function trackPageview(eventDetail) {
  recorders?.pageview(eventDetail);
}

/** Records a supplied link outside the sticky mobile CTA teaser. */
export function trackElementClick(element, eventDetail) {
  recorders?.trackElementClick?.(element, eventDetail);
}

/** Pushes a workflow name onto the recorder’s workflow stack. */
export function setCurrentWorkflow(workflowName) {
  recorders?.setCurrentWorkflow?.(workflowName);
}

(function (ahoy) {
  if (!ahoy) {
    return;
  } // ahoy not available in standalone embed contexts

  initializeAhoy(); // Call this before bailing out on a double load because ahoy.js will be loaded a second time and reset

  // Don't initialize more than once if included multiple times in different widgets
  if (window.Sparkle.Analytics) {
    recorders = window.Sparkle.Analytics[RECORDERS];
    return;
  }

  // The `analytics_batching` flag from `config/feature_flags.yml`. In ghost mode, every event goes through
  // `ahoy.track`, as with batching off. An event of a sampled visit also runs through batching as a dry run against
  // `/ahoy/ghost_events`, which has an outbox and fallback of its own. Where the dry run would hand events to
  // `ahoy.track`, it sends them as ghost receipts (see `sendGhostReceipts`), so
  // `Sparkle::Analytics::GhostReconciliation` counts them as delivered. The sample is checked for each event against
  // the current visit, because a page left open past the visit's duration moves to a new one.
  const batchingMode = window.SparkleConfig?.analytics?.batching;
  const ghost = batchingMode === "ghost";
  const ghostSamplePercent = window.SparkleConfig?.analytics?.ghostSamplePercent;
  const batching = batchingMode === "on" || ghost;

  // `ghostPending` holds the ids of the dry run's events not yet sent, stored or sent as ghost receipts.
  // `ghostHandedOff` holds the ids handed off while `recordEvent` appended an event, before it saw that event queued.
  const ghostPending = new Set();
  const ghostHandedOff = new Set();
  let ghostLeaveReported = false;
  let ghostRefusalReported = false;
  const GHOST_OUTBOX_PREFIX = "sparkle_analytics_ghost_outbox:";

  const analyticsFallback = new AnalyticsEventFallback(
    function (reason) {
      reportClientError((ghost ? "[ghost] " : "") + "Analytics fell back to sending events one at a time", {
        description: reason,
        baseUrl: getApiHost().apiHost || "",
        omitPage: true,
      });
    },
    ghost ? { key: "sparkle_analytics_ghost_fallback_until" } : undefined,
  );

  const analyticsTransport = new AnalyticsEventTransport(
    function () {
      return (getApiHost().apiHost || "") + (ghost ? "/ahoy/ghost_events" : "/ahoy/events");
    },
    {
      onRejected: handleRejectedBatch,
    },
  );

  // Ghost receipts go out on a transport of their own with no `onRejected`, so a receipt the ghost endpoint rejects is
  // dropped. `analyticsDelivery.reject` hands rejected events to its `track`, which in ghost mode is
  // `sendGhostReceipts`. It sends no probe either, because `sendGhostReceipts` drops the batches `send` returns, and a
  // probing transport returns every batch but the probe until the probe is answered.
  const ghostReceiptTransport = new AnalyticsEventTransport(
    function () {
      return (getApiHost().apiHost || "") + "/ahoy/ghost_events";
    },
    { probe: false },
  );

  const deliveryOptions = { batching: batching };

  if (ghost) {
    deliveryOptions.track = sendGhostReceipts;
    deliveryOptions.onHandOff = settleGhostEvents;
  }

  const analyticsOutbox = new AnalyticsEventOutbox(
    ghost ? { prefix: GHOST_OUTBOX_PREFIX, onRefused: reportGhostRefusal } : undefined,
  );
  const analyticsDelivery = new AnalyticsEventDelivery(
    ahoy,
    analyticsTransport,
    analyticsOutbox,
    analyticsFallback,
    deliveryOptions,
  );

  // Outside ghost mode nothing reads the dry run's outbox or its claims, so the page purges the batches and claims
  // ghost mode left there once they pass `maxAge`.
  if (!ghost) {
    const ghostOutbox = new AnalyticsEventOutbox({ prefix: GHOST_OUTBOX_PREFIX });

    ghostOutbox.purge();
    AnalyticsEventClaims.forOutbox(ghostOutbox).purge();
  }

  const analyticsEvents = new AnalyticsEventQueue(
    function (events, tokens, storedKey) {
      analyticsDelivery.send(events, tokens, storedKey);
    },
    {
      tokens: function () {
        return analyticsDelivery.currentTokens();
      },
      outbox: analyticsOutbox,
    },
  );

  analyticsFallback.onTrip(flushQueuedEvents);

  const autocompleteRecorders = {};
  const clickedResourceScope = [];

  const analytics = (window.Sparkle.Analytics = {
    workflows: [],
    initializeWorkflow: function (workflowName) {
      workflowName = workflowName || window.defaultSparkleAnalyticsWorkflow;
      this.setCurrentWorkflow(workflowName);
    },
    // Set the current workflow for the duration of the function. Can be used to execute an action belonging to a
    // separate workflow so that its events are logged to that workflow instead of the current one.
    withWorkflow: function (workflowName, fn) {
      this.setCurrentWorkflow(workflowName);

      try {
        fn();
      } finally {
        this.restorePreviousWorkflow();
      }
    },
    setCurrentWorkflow: function (workflowName) {
      this.workflows.unshift(workflowName);
    },
    restorePreviousWorkflow: function () {
      this.workflows.shift();
    },
    getCurrentWorkflow: function () {
      return this.workflows[0];
    },
    // Abandonment has its own directly called function because it cannot be triggered asynchronously via an event as
    // the page is unloaded because the browser will ignore events at that time.
    trackAbandonment: function (properties) {
      if (!shouldEnable()) {
        return;
      }

      recordEvent("$abandonment", trackingProperties(properties));
      // Called as the page unloads, so it cannot wait for the size or time flush.
      sendOnLeave();
    },
    // Manually emit a "$click" event for an element. Used when a click is handled programmatically
    // (e.g. mobile sticky CTA buttons that navigate via JS) and therefore never bubbles to the
    // global click handler below, so it would otherwise go untracked.
    trackElementClick: function (element, eventDetail) {
      if (!shouldEnable()) {
        return;
      }

      if (!element) {
        return;
      }

      recordEvent("$click", trackingProperties(elementProperties(element), eventDetail));
    },
  });

  if (!shouldEnable()) {
    receiveCommands(discardCommand);
    return;
  }

  recorders = {
    search: recordSearch,
    selection: recordSelection,
    autocomplete: recordAutocomplete,
    pageview: recordPageview,
    trackElementClick: recordClick,
    setCurrentWorkflow: analytics.setCurrentWorkflow.bind(analytics),
  };

  analytics[RECORDERS] = recorders;
  analytics.initializeWorkflow();
  trackPageView();
  trackClick();

  const activeVideoSessions = new Map();
  trackVideoPlay();

  trackPJAX();
  trackSearch();
  trackSearchSelect();
  trackAutocomplete();
  trackAutocompleteSelect();
  trackMessageShown();
  trackDecisionTreeStep();
  trackInteraction();
  const scrollDepthTracker = createScrollDepthTracker();
  scrollDepthTracker.startTracking();
  trackScrollDepth();
  deliverAcrossPageLeaves();
  analyticsDelivery.resume();
  releaseRealOutbox();
  receiveCommands(recordCommand);

  function initialPageview() {
    document.dispatchEvent(new CustomEvent("pageview.sparkle"));
  }

  // Deferred by a task so embed script load handlers can set the workflow before the first `$view`.
  function scheduleInitialPageview() {
    setTimeout(initialPageview, 0);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialPageview, { once: true });
  } else {
    scheduleInitialPageview();
  }

  // TRACKING FUNCTIONS

  function trackPageView() {
    function handlePageview(event) {
      recordPageview(event.detail);
    }

    onSparkleEvent("pageview.sparkle", handlePageview);
  }

  function recordPageview(eventDetail) {
    if (window.NOTRACK) {
      return;
    }

    recordEvent("$view", pageviewProperties(trackingProperties(eventDetail)));
  }

  function recordClick(link, detail) {
    // Sticky CTA teaser links record their own `$click`; inline teasers rely on this listener.
    if (!link || link.closest(".mobile-cta-sticky-teaser-wrapper")) {
      return;
    }

    const linkData = { resourceType: link.dataset.resourceType, resourceId: dataValue(link.dataset.resourceId) };
    if (linkData.resourceType && linkData.resourceId) {
      clickedResourceScope.unshift(linkData);
    }

    recordEvent("$click", trackingProperties(elementProperties(link), detail));
  }

  function trackClick() {
    function handleClick(event) {
      const path = event.composedPath();
      if (path.some((node) => window.Sparkle[FORWARDED_CLICKS]?.has(node))) {
        return;
      }

      recordClick(
        path.find((node) => node.matches?.("a")),
        event.detail,
      );
    }

    function handleLegacyClick(event) {
      recordClick(event.detail.link, event.detail.eventDetail);
    }

    document.addEventListener("click", handleClick);
    document.addEventListener("sparkle:legacy-click", handleLegacyClick);
  }

  // Records an "$interaction" for a control that declares `data-analytics-element` (its name in the analytics
  // data) and `data-analytics-interaction` (the action). A "select" control reports the value carried by its
  // `input` or `update-selected` event (or its own `value` for a native control); a "toggle" or "click" control
  // reports its `data-analytics-value`, if any, when clicked; a "close" control, when it dispatches `close`. A
  // "shown" element is recorded with its `data-analytics-value` once, when it is first visible on the page.
  // An ancestor's `data-analytics-scope` prefixes the element name, so one control can be told apart by where it
  // is used: `slot-picker-calendar-day` in a scope of `booking` is recorded as `booking-slot-picker-calendar-day`.
  function trackInteraction() {
    const SELECTOR = "[data-analytics-element][data-analytics-interaction]";
    const SHOWN_SELECTOR = '[data-analytics-element][data-analytics-interaction="shown"]';
    const shownElements = new WeakSet();

    // `ancestors` is the path from the control outwards; the nearest one carrying `data-resource-type` and
    // `data-resource-id` (a finder result card) becomes the event's record, ahead of the page-wide resource.
    function track(element, value, ancestors) {
      if (window.NOTRACK) {
        return;
      }

      const action = element.dataset.analyticsInteraction;
      const scope = ancestors.find(function (node) {
        return node.dataset && node.dataset.analyticsScope;
      });
      const name = element.dataset.analyticsElement;
      const properties = { element: scope ? `${scope.dataset.analyticsScope}-${name}` : name, action: action };

      // A cleared selection arrives as null; it is recorded as the empty value the "any" option carries.
      if (action === "select" && (value === undefined || value === null)) {
        value = "";
      }

      if (value !== undefined && value !== null) {
        properties.value = String(value);
      }

      const record = ancestors.find(function (node) {
        return node.dataset && node.dataset.resourceType && node.dataset.resourceId;
      });

      if (record) {
        properties.record_type = record.dataset.resourceType;
        properties.record_id = record.dataset.resourceId;
      }

      recordEvent("$interaction", trackingProperties(properties));
    }

    // sp-* controls dispatch a CustomEvent carrying the selection in `detail` as an array of emit arguments, and
    // a multi-select's first argument is itself an array of values. A native control's `input` event has no
    // payload (`detail` is the UIEvent counter, 0), so its value is read from the control.
    function selectedValue(event) {
      if (!(event instanceof CustomEvent)) {
        return event.target.value;
      }

      const value = Array.isArray(event.detail) ? event.detail[0] : event.detail;
      return Array.isArray(value) ? value[0] : value;
    }

    function handleSelect(event) {
      const element = event.target.closest && event.target.closest(SELECTOR);
      if (!element || element.dataset.analyticsInteraction !== "select") {
        return;
      }

      track(element, selectedValue(event), event.composedPath());
    }

    document.addEventListener("input", handleSelect);
    // `update-selected` is a Vue emit and does not bubble, so it is caught in the capture phase.
    document.addEventListener("update-selected", handleSelect, true);

    // Clicks are matched along `composedPath()`, so a control inside a custom element's shadow root is found, and
    // in the capture phase, because the finder's result views stop propagation before a click reaches the document.
    document.addEventListener(
      "click",
      function (event) {
        const element = event.composedPath().find(function (node) {
          return node.matches && node.matches(SELECTOR);
        });

        if (!element) {
          return;
        }

        const clickAction = element.dataset.analyticsInteraction;
        if (clickAction !== "toggle" && clickAction !== "click") {
          return;
        }

        track(element, element.dataset.analyticsValue, event.composedPath());
      },
      true,
    );

    // `close` is caught in the capture phase because a custom element's `close` emit does not bubble.
    function handleClose(event) {
      const element = event.target;
      if (!element.matches || !element.matches(SELECTOR) || element.dataset.analyticsInteraction !== "close") {
        return;
      }

      track(element, element.dataset.analyticsValue, event.composedPath());
    }

    document.addEventListener("close", handleClose, true);

    // Returns the path from `element` outwards across shadow roots, in the order `composedPath()` gives an event's.
    function ancestorsOf(element) {
      const ancestors = [];
      let node = element;

      while (node) {
        ancestors.push(node);
        node = node.parentNode || node.host;
      }

      return ancestors;
    }

    // Enter on a focused legacy control arrives as a jQuery-triggered click, which jQuery does not turn into a native
    // click on an anchor. The legacy bridge reports it as `sparkle:legacy-click`. An anchor with an `href` also gets
    // the browser's own click on Enter, which the click listener records, so only an anchor without one is taken here.
    function handleLegacyClick(event) {
      const link = event.detail.link;
      if (link.hasAttribute("href") || !link.matches(SELECTOR)) {
        return;
      }

      const clickAction = link.dataset.analyticsInteraction;
      if (clickAction !== "toggle" && clickAction !== "click") {
        return;
      }

      track(link, link.dataset.analyticsValue, ancestorsOf(link));
    }

    document.addEventListener("sparkle:legacy-click", handleLegacyClick);

    function isVisible(element) {
      if (typeof element.checkVisibility === "function") {
        return element.checkVisibility();
      }

      return element.getClientRects().length > 0;
    }

    // Records each "shown" element in the page once, when it is first visible. An element rendered into a hidden view
    // is recorded when the view is shown, which changes an ancestor's attributes without adding a node.
    function trackShown() {
      document.querySelectorAll(SHOWN_SELECTOR).forEach(function (element) {
        if (shownElements.has(element) || !isVisible(element)) {
          return;
        }

        shownElements.add(element);
        track(element, element.dataset.analyticsValue, ancestorsOf(element));
      });
    }

    new MutationObserver(trackShown).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class", "hidden", "open"],
    });
  }

  function trackDecisionTreeStep() {
    onSparkleEvent("decision-tree-step.sparkle", function (event) {
      if (window.NOTRACK) {
        return;
      }

      recordEvent("$decision-tree-step", trackingProperties(event.detail));
    });
  }

  function trackVideoPlay() {
    onSparkleEvent("embed:play.sparkle", embedVideoPlayHandler);
    onSparkleEvent("embed:pause.sparkle", videoCompletionHandler);
    onSparkleEvent("embed:ended.sparkle", videoCompletionHandler);
    document.addEventListener("play", htmlVideoPlayHandler, true);
    document.addEventListener("pause", htmlVideoCompletionHandler, true);
    document.addEventListener("ended", htmlVideoCompletionHandler, true);
    window.addEventListener("beforeunload", videoPageUnloadHandler);
  }

  function trackPJAX() {
    function handlePjaxSuccess(event) {
      const { args = [], ...detail } = event.detail || {};

      if (args[3]?.push) {
        recordEvent("$view", pageviewProperties(trackingProperties(detail)));
      }
    }

    document.addEventListener("sparkle:pjax-success", handlePjaxSuccess);
  }

  // For tracking queries
  function trackSearch() {
    function handleSearch(e, subtype, query, requestUrl, resultCount) {
      recordSearch(subtype, query, requestUrl, resultCount, e.detail);
    }

    onSparkleEvent("search.sparkle", handleSearch);
  }

  function recordSearch(subtype, query, requestUrl, resultCount, eventDetail) {
    scrollDepthTracker.resetScrollPercentages();
    recordEvent(
      "$search",
      trackingProperties(
        {
          subtype: subtype,
          query: query,
          url: requestUrl || null,
          originating_url: window.location.href,
          result_count: resultCount,
        },
        eventDetail,
      ),
    );
  }

  function trackSearchSelect() {
    // "record" is the item selected via the autocomplete results
    function handleSearchSelect(e, subtype, query, requestUrl, recordType, recordId) {
      recordSelection("$search-select", subtype, query, requestUrl, recordType, recordId, e.detail);
    }

    onSparkleEvent("search-select.sparkle", handleSearchSelect);
  }

  function recordSelection(name, subtype, query, requestUrl, recordType, recordId, eventDetail) {
    const properties = trackingProperties(
      {
        subtype: subtype,
        query: query,
        url: requestUrl,
        originating_url: window.location.href,
        record_type: recordType,
        record_id: recordId,
      },
      eventDetail,
    );
    recordEvent(name, properties);
  }

  // For tracking queries entered into an autocomplete (wait for 1 second of inactivity after typing to fire)
  function trackAutocomplete() {
    function handleAutocomplete(e, subtype, query, suggestions, requestUrl) {
      recordAutocomplete(subtype, query, suggestions, requestUrl, e.detail);
    }

    onSparkleEvent("autocomplete.sparkle", handleAutocomplete);
  }

  // Records one `$autocomplete` per subtype after a second without another one.
  function recordAutocomplete(subtype, query, suggestions, requestUrl, eventDetail) {
    let recorder = autocompleteRecorders[subtype];
    if (!recorder) {
      recorder = autocompleteRecorders[subtype] = debounce(function (
        subtype,
        query,
        suggestions,
        requestUrl,
        eventDetail,
      ) {
        recordEvent(
          "$autocomplete",
          trackingProperties(
            {
              subtype: subtype,
              query: query,
              url: requestUrl || null,
              originating_url: window.location.href,
              suggestions: suggestions,
            },
            eventDetail,
          ),
        );
      }, 1000);
    }

    recorder(subtype, query, suggestions, requestUrl, eventDetail);
  }

  // For tracking selection of an autocomplete item
  function trackAutocompleteSelect() {
    // "record" is the item selected via the autocomplete results
    function handleAutocompleteSelect(e, subtype, query, requestUrl, recordType, recordId) {
      recordSelection("$autocomplete-select", subtype, query, requestUrl, recordType, recordId, e.detail);
    }

    onSparkleEvent("autocomplete-select.sparkle", handleAutocompleteSelect);
  }

  function trackMessageShown() {
    onSparkleEvent("message-shown.sparkle", function (e) {
      const message = (e.target.textContent || "").trim().replace(/\s+/g, " ");
      const properties = trackingProperties({ message: message }, e.detail);
      recordEvent("$message-shown", properties);
    });
  }

  function trackScrollDepth() {
    onSparkleEvent("scroll-depth.sparkle", function (e, scrollPercent) {
      recordEvent("$scroll-depth", trackingProperties({ scroll_percent: scrollPercent }));
    });
  }

  // EVENT HANDLERS

  function embedVideoPlayHandler(e, subtype, title, duration) {
    videoPlayHandler(e.target, subtype, title, e.target.src, duration, e.detail);
  }

  function htmlVideoPlayHandler(e) {
    if (e.target.nodeName == "VIDEO") {
      videoPlayHandler(
        e.target,
        "html5",
        e.target.dataset.videoTitle,
        e.target.currentSrc,
        e.target.duration,
        videoDetail(e),
      );
    }
  }

  function videoDetail(event) {
    const embed = event.composedPath().find((node) => node.dataset?.searchUuid);
    return { ...event.detail, ...(embed ? { search_uuid: embed.dataset.searchUuid } : {}) };
  }

  function videoPlayHandler(element, subtype, title, url, duration, eventDetail) {
    const resource = clickedResourceScope[0] || {};
    let videoSession = activeVideoSessions.get(element);
    if (!videoSession) {
      videoSession = {
        subtype: subtype,
        title: title,
        url: url,
        duration: Math.round(duration),
        sessionId: randomUUID(),
        eventDetail: eventDetail,
      };

      recordEvent(
        "$video-play",
        trackingProperties(eventDetail, {
          subtype: subtype,
          video_title: title,
          video_url: url,
          video_session_id: videoSession.sessionId,
          video_duration: videoSession.duration,
          record_type: resource.resourceType,
          record_id: resource.resourceId,
        }),
      );
    }

    videoSession.startTime = new Date();
    activeVideoSessions.set(element, videoSession);
  }

  function htmlVideoCompletionHandler(e) {
    if (e.target.nodeName == "VIDEO") {
      videoCompletionHandler(e);
    }
  }

  function videoCompletionHandler(e) {
    sendQueuedVideoPlay(e.target, videoDetail(e));
    clickedResourceScope.shift();
  }

  function videoPageUnloadHandler() {
    activeVideoSessions.forEach(function (_, player) {
      sendQueuedVideoPlay(player);
    });
  }

  // HELPER FUNCTIONS

  function initializeAhoy() {
    ahoy.configure({
      urlPrefix: getApiHost().apiHost || "",
      cookieDomain: baseDomain(), // Track visits across campaigns subdomain
      startOnReady: false, // We want to avoid tracking visits in sandbox mode
    });

    if (shouldEnable()) {
      ahoy.start();
    }
  }

  function onSparkleEvent(eventName, handler) {
    function handleSparkleEvent(event) {
      const { args = [], ...detail } = event.detail || {};
      handler({ target: event.target, detail, composedPath: () => event.composedPath() }, ...args);
    }

    document.addEventListener(eventName, handleSparkleEvent);
  }

  function sendQueuedVideoPlay(currentPlayer, eventDetail) {
    const videoSession = activeVideoSessions.get(currentPlayer);
    if (!videoSession) {
      return;
    }

    recordEvent(
      "$video-stop",
      trackingProperties(videoSession.eventDetail, eventDetail, {
        subtype: videoSession.subtype,
        video_title: videoSession.title,
        video_url: videoSession.url,
        video_session_id: videoSession.sessionId,
        video_duration: videoSession.duration,
        video_play_duration: Math.round((new Date() - videoSession.startTime) / 1000),
      }),
    );

    activeVideoSessions.delete(currentPlayer);
  }

  // Drains the queue at both moments a browser offers on a page leave. `hidden` is the last state the Page
  // Lifecycle API calls reliably observable, and on mobile it is what a switch to another app fires. Their order
  // depends on how the page is left: closing a tab fires `visibilitychange` first, following a link fires
  // `pagehide` first. Both firing is harmless, because `flush` resends nothing. A page shown again, whether the tab
  // returns or the back/forward cache restores it, sends what the leave stored.
  function deliverAcrossPageLeaves() {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", sendOnLeave);
    window.addEventListener("pageshow", handlePageshow);
  }

  function handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      sendOnLeave();
    } else {
      analyticsDelivery.resume();
    }
  }

  function handlePageshow(event) {
    if (event.persisted) {
      analyticsDelivery.resume();
    }
  }

  // Marks the page as being left before flushing, so a flushed batch the visit is not ready for, or the browser
  // refuses, is stored for the next page.
  function sendOnLeave() {
    analyticsDelivery.stash();
    analyticsEvents.flush();
    checkGhostLeave();
  }

  // Reports, once a page, dry-run events still pending when the page is left. Does nothing outside ghost mode.
  function checkGhostLeave() {
    if (!ghost || ghostLeaveReported || ghostPending.size === 0) {
      return;
    }

    ghostLeaveReported = true;
    reportClientError("[ghost] Analytics batching left events unaccounted for at a page leave", {
      description: ghostPending.size + " events",
      baseUrl: getApiHost().apiHost || "",
      omitPage: true,
    });
  }

  // Reports, once a page, the first batch the dry run's outbox could not store or rewrite. Its events are then held in
  // memory only, so a page that dies without a leave event loses them, and the ghost report counts them as missed.
  function reportGhostRefusal(batch, reason) {
    if (ghostRefusalReported) {
      return;
    }

    ghostRefusalReported = true;
    reportClientError("[ghost] The analytics outbox could not store a batch", {
      description: reason + ", " + batch.events.length + " events",
      baseUrl: getApiHost().apiHost || "",
      omitPage: true,
    });
  }

  // Returns whether the visit with `visitToken` is in a ghost sample of `percent` percent, by the rule
  // `Sparkle::Analytics::GhostReconciliation.sampled?` applies.
  function inGhostSample(visitToken, percent) {
    return parseInt(String(visitToken).slice(0, 8), 16) % 100 < Number(percent);
  }

  // Sends `events` to the ghost endpoint as ghost receipts: each is marked `via: "ahoy"`, as an event batching would
  // have handed to `ahoy.track`. Waits on `ahoy.ready`, as `ahoy.track` does, so a receipt goes out only for an event
  // `ahoy.track` would have sent. Sends them under `batchTokens` when those carry a visit token, and otherwise under
  // the current visit. Sends nothing when that visit has no token, and drops what the browser refuses or the endpoint
  // rejects.
  function sendGhostReceipts(events, batchTokens) {
    ahoy.ready(function () {
      const tokens =
        batchTokens && batchTokens.visit_token
          ? batchTokens
          : { visit_token: ahoy.getVisitId(), visitor_token: ahoy.getVisitorId() };

      if (!tokens.visit_token) {
        return;
      }

      ghostReceiptTransport.send(
        events.map(function (event) {
          return Object.assign({}, event, { via: "ahoy" });
        }),
        tokens,
      );
    });
  }

  function settleGhostEvents(events) {
    events.forEach(function (event) {
      if (!ghostPending.delete(event.id)) {
        ghostHandedOff.add(event.id);
      }
    });
  }

  // Releases what the default outbox holds to `ahoy.track` at the paced rate, as a page with batching off does. Does
  // nothing outside ghost mode. In ghost mode the dry run keeps an outbox of its own, so nothing else reads the default
  // one, where pages stored batches while batching was on.
  function releaseRealOutbox() {
    if (!ghost) {
      return;
    }

    // With batching off the delivery only releases, so its fallback never trips and never reports.
    new AnalyticsEventDelivery(ahoy, analyticsTransport, new AnalyticsEventOutbox(), new AnalyticsEventFallback(noop), {
      batching: false,
    }).resume();
  }

  function noop() {}

  function handleRejectedBatch(events, status, tokens) {
    analyticsDelivery.reject(events, status, tokens);
  }

  function flushQueuedEvents() {
    analyticsEvents.flush();
  }

  // Records an event through the batching queue, or through `ahoy.track` where batching is off or its fallback is
  // active. An error appending to the queue trips the fallback and sends that event through `ahoy.track`. In ghost mode
  // the event always goes through `ahoy.track`, and for a sampled visit the dry run takes it as well, sending a ghost
  // receipt wherever batching would use `ahoy.track`.
  // @param time {Number} the milliseconds since the epoch at which the event happened, now when omitted. Every route
  //   sends the event with this time. The server keeps it only within `Ahoy.visit_duration` before now, and otherwise
  //   records the arrival time.
  function recordEvent(name, properties, time) {
    if (!batching) {
      ahoy.track(name, properties, toSeconds(time));
      return;
    }

    if (ghost) {
      ahoy.track(name, properties, toSeconds(time));

      // Read after `ahoy.track`, which opens a new visit when the last one has expired.
      if (!inGhostSample(ahoy.getVisitId(), ghostSamplePercent)) {
        return;
      }
    }

    if (analyticsFallback.active()) {
      trackOutsideBatching(name, properties, time);
      return;
    }

    try {
      const event = analyticsEvents.append(name, properties, time);
      if (ghost && !ghostHandedOff.delete(event.id)) {
        ghostPending.add(event.id);
      }

      // Ids handed off at any other time, such as those of a stored batch resent on a later page, are never looked
      // up, so the set keeps none past the append.
      ghostHandedOff.clear();
    } catch (e) {
      analyticsFallback.trip("batching threw " + e);
      trackOutsideBatching(name, properties, time);
    }
  }

  // Sends an event batching cannot take through `ahoy.track`. In ghost mode, where `recordEvent` has already passed it
  // to `ahoy.track`, it goes out as a ghost receipt instead.
  function trackOutsideBatching(name, properties, time) {
    if (ghost) {
      sendGhostReceipts([{ id: randomUUID(), name: name, properties: properties, time: (time || Date.now()) / 1000 }]);
    } else {
      ahoy.track(name, properties, toSeconds(time));
    }
  }

  // Converts `time` from milliseconds to the seconds `ahoy.track` takes. Returns undefined when `time` is omitted, so
  // `ahoy.track` uses now.
  function toSeconds(time) {
    return time ? time / 1000 : undefined;
  }

  // Replaces `window.sparkleAnalytics` with a function that passes each call to `handle`, then passes `handle` each
  // call the `Sparkle::Analytics::Snippet` stub queued before this module ran. A call is `[args, time, url, title]`:
  // the arguments it was made with, and the milliseconds since the epoch, URL and title at the moment it was made.
  // That is the queue format of stub version 1, the only version. A stub without a version is read as version 1, and
  // a queue from any other version is discarded with a console warning.
  function receiveCommands(handle) {
    const stub = window.sparkleAnalytics;
    const queued = (stub && stub.q) || [];
    const version = (stub && stub.v) || 1;

    window.sparkleAnalytics = function () {
      handle([arguments, Date.now(), window.location.href, document.title]);
    };

    if (version !== 1) {
      console.warn("sparkleAnalytics: discarded calls queued by unknown stub version " + JSON.stringify(version));
      return;
    }

    queued.forEach(handle);
  }

  // Records one analytics command under the time and URL at which the call was made:
  //   - `track(name, properties)`: records the event `name`
  //   - `page(properties)`: records a `$view`, with the `title` and `page` (path) of the call as well
  // Properties the call passes are merged last, so they override the captured `url`, `title` and `page`. An unknown
  // command, and a `track` without a name, are ignored with a console warning. Unlike the event listeners, a command
  // is recorded while `window.NOTRACK` is set.
  function recordCommand(call) {
    const args = call[0];
    const time = call[1];
    const page = { url: call[2], title: call[3], page: pathOf(call[2]) };

    if (args[0] === "track" && args[1]) {
      recordEvent(args[1], trackingProperties({ url: page.url }, args[2]), time);
    } else if (args[0] === "page") {
      recordEvent("$view", trackingProperties(page, args[1]), time);
    } else if (args[0] === "track") {
      console.warn("sparkleAnalytics: ignored a track command with no event name", args);
    } else {
      console.warn("sparkleAnalytics: ignored unknown command " + JSON.stringify(args[0]), args);
    }
  }

  function pathOf(url) {
    try {
      return new URL(url).pathname;
    } catch {
      return window.location.pathname;
    }
  }

  function discardCommand() {}

  // Returns the properties with the `url`, `title` and `page` that `ahoy.trackView` adds. A `$view` posted
  // through the queue does not go through `ahoy.trackView`.
  function pageviewProperties(properties) {
    return { url: window.location.href, title: document.title, page: window.location.pathname, ...properties };
  }

  function normalizeText(value) {
    return (value || "").replace(/[\s\r\n]+/g, " ").trim();
  }

  function elementText(element) {
    const text = normalizeText(element.textContent);
    if (text) {
      return text;
    }

    const label = normalizeText(element.getAttribute("aria-label") || element.getAttribute("title"));
    if (label) {
      return label;
    }

    const host = element.getRootNode()?.host;
    if (!host) {
      return "";
    }
    return (
      normalizeText(host.textContent) || normalizeText(host.getAttribute("aria-label") || host.getAttribute("title"))
    );
  }

  function dataValue(value) {
    return value !== undefined && String(Number(value)) === value ? Number(value) : value;
  }

  function elementProperties(element) {
    return {
      id: element.getAttribute("id") ?? undefined,
      tag: element.tagName.toLowerCase(),
      class: element.getAttribute("class") ?? undefined,
      text: elementText(element),
      href: element.getAttribute("href") ?? undefined,
    };
  }

  // An undefined value keeps the earlier source’s property.
  function mergeProperties(...sources) {
    return Object.assign(
      {},
      ...sources.map((source) =>
        Object.fromEntries(Object.entries(source || {}).filter(([, value]) => value !== undefined)),
      ),
    );
  }

  /**
   * Build standard tracking properties for the analytics event.
   * @param  {...Object} arg1, properties object to merge into the standard tracking properties
   */
  function trackingProperties() {
    const additionalProperties = mergeProperties(...arguments);

    const sparkleProperties = { url: window.location.href };

    if (window.RESOURCE || (additionalProperties.record_id && additionalProperties.record_type)) {
      sparkleProperties.record_type = additionalProperties.record_type || window.RESOURCE.analyticsType;
      sparkleProperties.record_id = additionalProperties.record_id || window.RESOURCE.analyticsId;
    }

    if (window.SOURCE_ID) {
      sparkleProperties.source_id = window.SOURCE_ID;
    }

    if (window.CANDIDATE_ID) {
      sparkleProperties.candidate_id = window.CANDIDATE_ID;
    }

    sparkleProperties.workflow = window.Sparkle.Analytics.getCurrentWorkflow();

    return mergeProperties(sparkleProperties, additionalProperties);
  }

  // http://rossscrivener.co.uk/blog/javascript-get-domain-exclude-subdomain
  function baseDomain() {
    let i = 0;
    let domain = document.domain || window.location.hostname;
    const p = domain.split(".");
    const s = "_gd" + new Date().getTime();

    while (i < p.length - 1 && document.cookie.indexOf(s + "=" + s) == -1) {
      domain = p.slice(-1 - ++i).join(".");
      document.cookie = s + "=" + s + ";domain=" + domain + ";";
    }

    document.cookie = s + "=;expires=Thu, 01 Jan 1970 00:00:01 GMT;domain=" + domain + ";";
    return domain;
  }

  function shouldEnable() {
    return window.sparkleAnalyticsEnabled !== false && !isSandbox();
  }

  function isSandbox() {
    const subdomain = window.SparkleConfig?.sandbox?.subdomain;
    if (!subdomain) {
      return false;
    }

    // `window.top.location` throws a SecurityError when the top frame
    // is cross-origin (admin preview iframes, third-party customer
    // embeds). In that case we can't introspect the top hostname — but
    // neither case is meant to be the "sandbox admin browsing its own
    // site" path that this check was written for, so fall through to
    // `false` and let the explicit `sparkleAnalyticsEnabled` flag
    // control whether ahoy actually fires.
    try {
      return new RegExp(subdomain).test(window.top.location.hostname);
    } catch {
      return false;
    }
  }
})(window.ahoy);
